import type { AiRolePlayEventPayload, AudioEventPayload, EndEventPayload } from "@expert/expert-university";
import { useExpertUniversityConfiguration, useExpertUniversityStore, WsEvents } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useEffect, useMemo } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { env } from "@/config";
import { isSimulatedVoiceTask, useActiveTask } from "@/sdk";
import { useSessionStore } from "@/sdk/sessions/session.store";

export function useExpertUniversityWebsocket() {
    const logger = getLogger({ module: "Expert University Websocket" });
    const {
        audioHeader,
        eventBus: expertUniversitySdkEventBus,
        setWSFunctions,
        reconnect,
        setReconnect,
    } = useExpertUniversityStore.getState();
    const { taskInSandbox } = useExpertUniversityConfiguration();
    const activeTask = useActiveTask();
    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    const { profileId, sessionId } = activeExpertUniversityTask ?? {};

    const onOpen = () => {
        logger.info({ sessionId, profileId }, "Opening websocket connection");
        if (audioHeader) {
            sendMessage?.(audioHeader);
        }
        const websocketUrl = getWebSocket()?.url;
        if (expertUniversitySdkEventBus && websocketUrl) {
            expertUniversitySdkEventBus.emit("websocket-opened", { websocketUrl });
        }
    };

    const onClose = (event: CloseEvent) => {
        const websocketUrl = getWebSocket()?.url;
        if (expertUniversitySdkEventBus && websocketUrl) {
            expertUniversitySdkEventBus.emit("websocket-closed", { websocketUrl });
        }
        setReconnect(true);
        logger.info({ sessionId, profileId, websocketUrl, ...event }, "Websocket connection closed");
    };

    const onMessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data as string) as AiRolePlayEventPayload;
        if (data.event === WsEvents.ERROR || data.event === WsEvents.END) {
            trackFullStoryEvent(`Websocket ${data.event} event received`, {
                data: data.event === WsEvents.AUDIO ? "Audio data omitted" : data,
                sessionId,
                lastEventId: event.lastEventId,
                origin: event.origin,
                eventCategory: data.event,
            });
            logger.info(
                {
                    data: data.event === WsEvents.AUDIO ? "Audio data omitted" : data,
                    sessionId,
                    profileId,
                    lastEventId: event.lastEventId,
                    origin: event.origin,
                    eventCategory: data.event,
                    endReasonType:
                        data.event === WsEvents.END ? (data.eventPayload as EndEventPayload).reasonType : undefined,
                },
                `Received ${data.event} event`,
            );
        }

        if (data.event === WsEvents.AUDIO && expertUniversitySdkEventBus) {
            const payload = data.eventPayload as AudioEventPayload;
            expertUniversitySdkEventBus.emit("audio-event-received", { audioBlob: payload.audioBlob });
        }
    };
    const { readyState, sendMessage, getWebSocket } = useWebSocket(
        env.expertUniversityWebsocketUrl ?? null,
        {
            queryParams:
                sessionId && profileId
                    ? { sessionId, CustomerProfile: profileId, reconnecting: `${reconnect}` }
                    : undefined,
            shouldReconnect: (_closeEvent) => !!sessionId && !!profileId,
            reconnectAttempts: 10,
            reconnectInterval: 5000,
            retryOnError: true,
            onOpen,
            onClose,
            onMessage,
            onReconnectStop: () => {
                logger.info({ sessionId, profileId }, "Stopped attempting to websocket");
            },
            onError: (error) => {
                logger.info({ error, sessionId, profileId }, "WebSocket error");
                return;
            },
        },
        !!sessionId && !!profileId && !taskInSandbox, //Only connect if we have a session and profile and no task in sandbox mode
    );
    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            setWSFunctions(sendMessage, getWebSocket);
        }
    }, [getWebSocket, readyState, sendMessage, setWSFunctions]);

    useEffect(() => {
        //End any session with non active simulated voice task
        if (!activeExpertUniversityTask && activeTask?.id.includes("test-training")) {
            trackFullStoryEvent(`CallUnexpectedlyEnded`, { sessionId });

            logger.info({ sessionId, profileId }, "Call unexpectedly ended, ending session");

            useSessionStore.getState().endSession(activeTask.id, "CallUnexpectedlyEnded");
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [activeExpertUniversityTask, activeTask, logger.info, sessionId, profileId]);
}
